import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useConversations } from 'chat/conversations/queries/list'
import { useMembers } from 'chat/members/queries/list'
import { useRecipients } from 'chat/members/queries/recipients'
import { useFileAcknowledgementUserIds } from 'file_folder_repository/queries/list'
import { ShareResourceRecipientsScreenProps } from 'home/stack/types'
import { useEffect, useLayoutEffect, useMemo } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { useUser } from 'users/queries/detail'
import { useShareResourceContext } from '../context'
import { ShareResourceRecipientsView } from './views'

export function ShareResourceRecipientsScreen({
  navigation,
  route,
}: ShareResourceRecipientsScreenProps) {
  const { handleNavigation } = useRoutingContext()
  const { resourceId, resourceType, requestAcknowledgement, conversationId } =
    route.params
  const { theme } = useTheme()
  const conversationsQuery = useConversations()
  const recipientsQuery = useRecipients()
  const membersQuery = useMembers(
    conversationId ?? '',
    !!conversationId && requestAcknowledgement
  )
  const userQuery = useUser()
  const acknowledgementUserIdsQuery = useFileAcknowledgementUserIds(resourceId)
  const {
    selectedConversationIds,
    selectedRecipientIds,
    setSelectedConversationIds,
    setSelectedRecipientIds,
    setSelectedResourceId,
    setSelectedResourceType,
  } = useShareResourceContext()

  const memberRecipients = useMemo(() => {
    if (!requestAcknowledgement) {
      return recipientsQuery.data
    }
    return (
      membersQuery.data?.filter(member => member.id !== userQuery.data?.id) ||
      []
    )
  }, [
    membersQuery.data,
    recipientsQuery.data,
    requestAcknowledgement,
    userQuery.data,
  ])

  const acknowledgedUserIds = useMemo(() => {
    if (!requestAcknowledgement) {
      return []
    }
    return acknowledgementUserIdsQuery.data || []
  }, [requestAcknowledgement, acknowledgementUserIdsQuery.data])

  const conversationRecipients = useMemo(
    () => (requestAcknowledgement ? [] : conversationsQuery.data),
    [conversationsQuery.data, requestAcknowledgement]
  )

  useLayoutEffect(() => {
    showHeaderTitle(navigation, t('chooseRecipients'))
  }, [navigation])

  useEffect(() => {
    setSelectedResourceId(resourceId)
    setSelectedResourceType(resourceType)
  }, [resourceId, resourceType, setSelectedResourceId, setSelectedResourceType])

  useLayoutEffect(() => {
    showHeaderButton({
      navigation,
      onPress: () => {
        handleNavigation({
          routeName: 'share_resource_details',
          routeParams: { requestAcknowledgement, conversationId },
        })
      },
      title: t('next'),
      disabled:
        selectedRecipientIds.length === 0 &&
        selectedConversationIds.length === 0,
      theme,
    })
  }, [
    conversationId,
    handleNavigation,
    navigation,
    requestAcknowledgement,
    resourceId,
    resourceType,
    selectedConversationIds.length,
    selectedRecipientIds.length,
    theme,
  ])

  return (
    <StatusBoundary
      isLoading={
        conversationsQuery.isLoading || membersQuery.isLoading
      }
      error={conversationsQuery.error}
    >
      <ShareResourceRecipientsView
        user={userQuery.data}
        conversationRecipients={conversationRecipients}
        memberRecipients={memberRecipients}
        selectedConversationIds={selectedConversationIds}
        selectedRecipientIds={selectedRecipientIds}
        setSelectedConversationIds={setSelectedConversationIds}
        setSelectedRecipientIds={setSelectedRecipientIds}
        acknowledgedUserIds={acknowledgedUserIds}
      />
    </StatusBoundary>
  )
}
